import React from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Info } from "lucide-react";
import CountUp from "react-countup";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import type { DailyXp } from "@/types/courses";

interface EarnedXPCardProps {
  totalXp: number;
  todayXp: number;
  dailyTarget: number;
  weeklyXp: DailyXp[];
}

export const EarnedXPCard: React.FC<EarnedXPCardProps> = ({
  totalXp,
  todayXp,
  dailyTarget,
  weeklyXp,
}) => {
  const todayPercent = Math.min((todayXp / dailyTarget) * 100, 100);
  const maxXp = Math.max(...weeklyXp.map((d) => d.xp), 1);

  // Find today's entry
  const todayEntry = weeklyXp[weeklyXp.length - 1];

  // Y-axis ticks: round maxXp up to nearest 100 then create ~3 ticks
  const yMax = Math.ceil(maxXp / 100) * 100;
  const yTicks = [yMax, Math.round(yMax / 2), 0];

  return (
    <Card className="border-border/60">
      <CardContent className="p-5 space-y-5">
        {/* Title row: "Earned XP (i)" on left, "213 XP" on right */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1.5">
            <span className="text-base font-semibold text-foreground">
              Earned XP
            </span>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Info className="h-4 w-4 text-muted-foreground cursor-help" />
                </TooltipTrigger>
                <TooltipContent side="right" className="max-w-[200px]">
                  <p className="text-xs">
                    Total XP earned across all lessons and activities in this
                    course.
                  </p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
          <span className="text-base font-semibold text-foreground">
            <CountUp end={totalXp} duration={1} separator="," /> XP
          </span>
        </div>

        {/* Today's Target */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-sm text-muted-foreground">Today's Target</span>
            <span className="text-sm font-medium text-foreground">
              {todayXp}/{dailyTarget} XP
            </span>
          </div>
          <Progress value={todayPercent} className="h-2.5 bg-correct/20 [&>div]:bg-correct" />
        </div>

        {/* Today label + XP value */}
        <div>
          <p className="text-sm text-muted-foreground">Today</p>
          <p className="text-xl font-bold text-foreground">
            {todayEntry?.xp ?? 0} XP
          </p>
        </div>

        {/* Bar chart with Y-axis labels */}
        <div className="flex gap-2">
          {/* Y-axis labels */}
          <div className="flex flex-col justify-between h-28 py-0.5 shrink-0">
            {yTicks.map((tick) => (
              <span
                key={tick}
                className="text-[10px] text-muted-foreground leading-none text-right w-8"
              >
                {tick}xp
              </span>
            ))}
          </div>

          {/* Bars + day labels */}
          <div className="flex-1 flex flex-col">
            <div className="flex items-end gap-2 h-28">
              {weeklyXp.map((day, i) => {
                const height = Math.max((day.xp / yMax) * 100, 3);
                const isToday = i === weeklyXp.length - 1;
                return (
                  <div
                    key={day.date}
                    className="flex-1 flex flex-col items-center justify-end h-full"
                  >
                    <div
                      className={`w-full rounded-sm transition-all ${
                        isToday ? "bg-success" : "bg-muted-foreground/15"
                      }`}
                      style={{ height: `${height}%`, minHeight: "3px" }}
                    />
                  </div>
                );
              })}
            </div>
            <div className="flex gap-2 mt-1.5">
              {weeklyXp.map((day) => {
                const label = new Date(day.date).toLocaleDateString("en", {
                  weekday: "short",
                });
                return (
                  <span
                    key={day.date}
                    className="flex-1 text-center text-[10px] text-muted-foreground"
                  >
                    {label}
                  </span>
                );
              })}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
